import { isVideoAccessible } from '$lib/utils/checkVideoAccess';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { User } from '$lib/types/user';

export async function getPlaylist(
	balancedSupabase: SupabaseClient,
	playlistId: string,
	user: User | null
) {
	const queryStringOld = `
        id,
        name,
        playlists_videos(
            order,
            videos:videos(
            i, id, free, name, permalink_slug, pick, level, public, exclude, duration, language, vimeo_id, sector_id, created_at, category_id, description, material_id, public_image_url, vimeo_created_time, vimeo_release_time, vimeo_modified_time
            )
        )
    `;

	const queryString = `
        id,
        name,
        playlists_videos(
            order,
            videos:videos(
            i, id, free, name, permalink_slug, public, exclude, duration, vimeo_id, public_image_url
            )
        )
    `;

	const query = balancedSupabase.from('playlists').select(queryString);

	const { data: playlist, error } = await query
		.eq('id', playlistId)
		.order('order', { ascending: true, referencedTable: 'playlists_videos' });

	if (error) {
		console.error(error);
		return [];
	}

	// Flatten the videos from the nested structure
	const orderedVideos = playlist[0]?.playlists_videos.map((pv) => pv.videos);

	const orderedVideosUpdate = orderedVideos.map((video) => ({
		...video,
		locked: !isVideoAccessible(video, user)
	}));

	const result = {
		id: playlist[0].id,
		name: playlist[0].name,
		videos: orderedVideosUpdate
	};

	return result;
}

export const getPlaylists = async (
	balancedSupabase: SupabaseClient,
	user: User | null,
	start: number,
	end: number
) => {
	console.log('getPlaylists called');
	const queryString = `
        *,
        playlists_videos(video_id, order),
        videos!playlists_videos(i, id, free, name, permalink_slug, exclude, vimeo_id, public_image_url)
    `;

	const {
		data: playlists,
		error,
		count
	} = await balancedSupabase
		.from('playlists')
		.select(queryString, { count: 'exact' })
		.is('user_id', null)
		.eq('exclude', false)
		.range(start, end)
		.order('order', { ascending: true })
		.order('created_at', { ascending: false });

	if (error) {
		console.error(error);
		return { data: [], count: 0 };
	}

	return {
		data: playlists.map((playlist) => {
			// Create a map of video_id to order
			const orderMap = new Map(playlist.playlists_videos.map((pv) => [pv.video_id, pv.order]));

			// Add order to videos and check accessibility
			const videosWithOrder = playlist.videos.map((video) => ({
				...video,
				order: orderMap.get(video.id) ?? Infinity,
				locked: !isVideoAccessible(video, user)
			}));

			// Sort videos by order
			const sortedVideos = videosWithOrder.sort((a, b) => a.order - b.order);

			return {
				...playlist,
				videos: sortedVideos
			};
		}),
		count
	};
};
